
import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ParameterGroup, Protocol, ProtocolParameter, ProtocolResourceCategory, ProtocolStep } from './protocol-service.types';
import { PagedResponse } from '@bioconnect-ui/shared-service';


export interface ProtocolConfig {
  restServer: string;
  wijmoLicenseKey: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProtocolService {
  constructor(private httpClient: HttpClient,
    @Inject('protocolServiceConfig') private protocolServiceConfig: ProtocolConfig) { }

  private restServer = this.protocolServiceConfig.restServer;


  getAllProtocols(): Observable<Protocol[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/protocol/?page_size=500`
    ).pipe(map((result) => <Protocol[]>result.results));
  }

  getProtocols(
    filter: string,
    pageNumber: number,
    sort: string,
    direction: string,
    pageSize: number,
    permission = 'can_view'
  ): Observable<PagedResponse> {
    let url = `${this.restServer}/metadata_repository/protocol/`;
    url += `?filter=${filter}&page=${pageNumber}&page_size=${pageSize}&sort=${sort}&direction=${direction}&permission=${permission}`;
    return this.httpClient.get<PagedResponse>(url);
  }

  getProtocolsByInvestigation(investigation_id: number): Observable<Protocol[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/protocol/by_investigation/${investigation_id}/?page_size=500`
    ).pipe(map((result) => <Protocol[]>result.results));
  }
  
  getProtocolsByStudy(study_id: number): Observable<Protocol[]> {
    const url = `${this.restServer}/metadata_repository/protocol/by_study/${study_id}/?page_size=500`;
    return this.httpClient.get<PagedResponse>(url)
      .pipe(map((result) => <Protocol[]>result.results));
  }

  getProtocol(id: number): Observable<Protocol> {
    return this.httpClient.get<Protocol>(
      `${this.restServer}/metadata_repository/protocol/${id}/`
    );
  }

  addProtocol(protocol: Protocol): Observable<Protocol> {
    return this.httpClient.post<Protocol>(
      `${this.restServer}/metadata_repository/protocol/`,
      protocol
    );
  }

  updateProtocol(protocol: Protocol): Observable<Protocol> {
    return this.httpClient.patch<Protocol>(
      `${this.restServer}/metadata_repository/protocol/${protocol.id}/`,
      protocol
    );
  }

  deleteProtocol(id: number) {
    return this.httpClient.delete(
      `${this.restServer}/metadata_repository/protocol/${id}/`
    )
  }

  updateParameterGroups(protocolId: number, groups: ParameterGroup[]): Observable<ParameterGroup[]> {
    return this.httpClient.patch<ParameterGroup[]>(
      `${this.restServer}/metadata_repository/protocol/${protocolId}/parameter_groups/`,
      groups
    );
  }

  deleteParameterGroup(id: number): Observable<object> {
    return this.httpClient.delete(
      `${this.restServer}/metadata_repository/parameter_group/${id}/`
    );
  }

  getProtocolParameter(id: number): Observable<ProtocolParameter> {
    return this.httpClient.get<ProtocolParameter>(
      `${this.restServer}/metadata_repository/protocol_parameter/${id}/`
    );
  }

  deleteProtocolParameter(id: number): Observable<object> {
    return this.httpClient.delete(
      `${this.restServer}/metadata_repository/protocol_parameter/${id}/`
    );
  }

  updateProtocolSteps(protocolId: number, steps: ProtocolStep[]): Observable<ProtocolStep[]> {
    return this.httpClient.patch<ProtocolStep[]>(
      `${this.restServer}/metadata_repository/protocol/${protocolId}/protocol_steps/`,
      steps
    );
  }

  deleteProtocolStep(id: number): Observable<object> {
    return this.httpClient.delete(
      `${this.restServer}/metadata_repository/protocol_step/${id}/`
    );
  }


  deleteProtocolResourceCategory(id: number): Observable<object> {
    return this.httpClient.delete(
      `${this.restServer}/metadata_repository/protocol_resource_category/${id}/`
    );
  }

  updateProtocolResources(protocolId: number, resources: ProtocolResourceCategory[]): Observable<ProtocolResourceCategory[]> {
    return this.httpClient.patch<ProtocolResourceCategory[]>(
      `${this.restServer}/metadata_repository/protocol/${protocolId}/resource_categories/`,
      resources
    );
  }
}